import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Activity, AlertTriangle, ArrowUpRight, Bell, ListTodo, Building2, Globe2, Info, Lock, Network, ShieldAlert, ShieldCheck, UserCheck } from "lucide-react";
import { api, fmtUSD } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { ChatPanel } from "@/components/meridian/ChatPanel";
import { DeptDashboards } from "@/components/meridian/DeptDashboards";

const DEPTS = [
  { key: "lending", name: "Credit Lending (XAI)", desc: "Explainable, auditable CRE credit recommendations", icon: Building2, live: true },
  { key: "wire", name: "Wire Operations & AML", desc: "Volume monitoring, filters, AML exceptions", icon: ShieldAlert, live: true },
  { key: "xb", name: "Cross-Border Compliance", desc: "US vs UK / EU / Mexico rule gap analysis", icon: Globe2, live: true },
  { key: "grc", name: "GRC & Audit", desc: "Issues, controls, access reviews", icon: ShieldCheck, live: true },
  { key: "kyc", name: "KYC & Due Diligence", desc: "CDD checklist, OFAC screening, risk rating, sign-off", icon: UserCheck, live: true },
  { key: "systems", name: "Systems & Controls", desc: "Every agent, the systems behind it and the role-based control layer", icon: Network, live: true },
];

function Metric({ label, value, id, icon: Icon }) {
  return (
    <div className="panel panel-hover p-5 relative overflow-hidden">
      <div className="flex items-center justify-between"><span className="eyebrow">{label}</span><span className="icon-chip w-8 h-8 rounded-lg"><Icon className="w-4 h-4" /></span></div>
      <div data-testid={`home-metric-${id}`} className="num text-2xl font-semibold text-slate-900 mt-3">{value}</div>
      <div className="absolute inset-x-0 bottom-0 h-0.5 bg-gradient-to-r from-sky-500 via-sky-400 to-transparent opacity-60" />
    </div>
  );
}

const LEVEL = { critical: [AlertTriangle, "text-red-600"], warning: [AlertTriangle, "text-amber-600"], info: [Info, "text-sky-600"] };

export default function Home() {
  const [data, setData] = useState(null);
  const { refreshTasks } = useApp();
  const nav = useNavigate();
  const load = useCallback(() => api.get("/home").then((r) => setData(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const onDept = (k) => {
    if (k === "wire") nav("/task/task-wire-ops");
    if (k === "xb") nav("/task/task-compliance");
    if (k === "lending") nav("/lending");
    if (k === "grc") nav("/grc");
    if (k === "kyc") nav("/task/task-kyc");
    if (k === "systems") nav("/systems");
  };
  const m = data?.metrics;
  return (
    <div className="flex flex-1 min-w-0" data-testid="home-page">
      <div className="flex-1 min-w-0 overflow-y-auto thin-scroll grid-bg">
        <div className="max-w-6xl px-5 sm:px-8 py-8 space-y-8">
          <div className="rise">
            <div className="chip border-sky-200 bg-sky-50 text-sky-700 mb-3"><span className="w-1.5 h-1.5 rounded-full bg-sky-500" />Workspace overview</div>
            <h1 className="font-display text-4xl lg:text-5xl font-extrabold tracking-tight hero-title pb-1">Good morning, analyst.</h1>
            <p className="text-sm text-slate-600 mt-2 max-w-xl">Lending and wire operations in one place. Ask in plain English, and every figure links back to its source.</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 stagger">
            <Metric id="open-tasks" icon={ListTodo} label="Open tasks" value={m?.open_tasks ?? "—"} />
            <Metric id="pipeline" icon={Building2} label="Loan pipeline" value={m ? fmtUSD(m.pipeline_value, true) : "—"} />
            <Metric id="wires-today" icon={Activity} label="Wires (24h)" value={m ? `${m.wires_today} · ${fmtUSD(m.wire_value_today, true)}` : "—"} />
            <Metric id="open-alerts" icon={ShieldAlert} label="Open AML alerts" value={m?.open_alerts ?? "—"} />
          </div>
          <DeptDashboards />
          <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
            {DEPTS.map((d, i) => (
              <button key={d.key} data-testid={`dept-tile-${d.key}`} disabled={!d.live} onClick={() => onDept(d.key)}
                style={{ animationDelay: `${100 + i * 50}ms` }}
                className="rise group text-left panel panel-hover p-6 disabled:opacity-50">
                <div className="flex justify-between">
                  <span className="icon-chip group-hover:bg-sky-600 group-hover:text-white transition-colors"><d.icon className="w-[18px] h-[18px]" /></span>
                  {d.live ? <ArrowUpRight className="w-4 h-4 text-slate-400 group-hover:text-sky-600 transition-colors" /> : <span className="text-[10px] flex items-center gap-1 text-slate-500"><Lock className="w-3 h-3" />Phase 2</span>}
                </div>
                <div className="font-display text-lg font-bold text-slate-900 mt-5">{d.name}</div>
                <div className="text-xs text-slate-500 mt-1">{d.desc}</div>
              </button>
            ))}
          </div>
          <div className="grid lg:grid-cols-5 gap-4">
            <div className="lg:col-span-3 panel overflow-hidden">
              <div className="px-5 py-3.5 border-b border-slate-100 text-sm font-semibold text-slate-900">Task queue</div>
              <div className="divide-y divide-slate-100" data-testid="home-task-queue">
                {data?.tasks.map((t) => (
                  <button key={t.id} data-testid={`queue-task-${t.id}`} onClick={() => nav(`/task/${t.id}`)} className="w-full flex justify-between items-center px-5 py-3 text-left hover:bg-sky-50/50 transition-colors">
                    <div><div className="text-sm text-slate-900">{t.title}</div><div className="text-xs text-slate-500">{t.subtitle}</div></div>
                    <span className="chip border-slate-200 bg-slate-50 text-slate-700">{t.status}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="lg:col-span-2 panel overflow-hidden">
              <div className="px-5 py-3.5 border-b border-slate-100 text-sm font-semibold text-slate-900 flex items-center gap-2"><Bell className="w-4 h-4 text-sky-600" />Notifications</div>
              <div className="divide-y divide-slate-100">
                {data?.notifications.map((n, i) => {
                  const [Icon, c] = LEVEL[n.level];
                  return (
                    <button key={i} data-testid={`notification-${i}`} disabled={!n.task_id} onClick={() => nav(`/task/${n.task_id}`)} className="w-full flex gap-3 px-5 py-3 text-left text-xs text-slate-700 hover:bg-sky-50/50 transition-colors">
                      <Icon className={`w-4 h-4 shrink-0 ${c}`} />{n.text}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="w-[380px] shrink-0 border-l border-slate-200/80 bg-white hidden lg:block">
        <ChatPanel taskId="task-assistant" title="OmniFi Assistant"
          suggestions={["Start underwriting for Dallas Office Tower and Frisco Retail Plaza", "Start the $10M Austin Commerce Center example", "Open the wire monitoring dashboard"]}
          onDone={() => { load(); refreshTasks(); }} />
      </div>
    </div>
  );
}
